import { useContext, useRef } from "react";
import clsx from "clsx";
import { motion, useInView } from "framer-motion";
import { context } from "@/Layout";
import { Background } from "@/components/utils/Background";
import { Button } from "@/components/utils/Button";
import { Eyebrow } from "@/components/utils/Eyebrow";
import { usePadding } from "@/components/utils/Padding";
import { Media } from "@/components/utils/Media";
import { Text } from "@/components/utils/Text";
import { RichContentPropTypes } from "./propTypes";
import "./styles.scss";

/* #META
 {
  "NAME": "RichContent",
  "CLASSNAME": "rich-content-component",
  "URL": "http://nicasource.local"
 }
#ENDMETA */

const RichContent = ({ data }) => {
  // Component
  const { component, show_secondary_button, buttons_alignment, secondary_button_component } =
    data ?? {};

  // Use nullish coalescing to ensure data and its nested objects are defined
  const {
    // Background component
    background_component: backgroundComponent = {},
    // Padding component
    padding_component: paddingComponent = {},
    // Eyebrow component
    eyebrow_component: eyebrowComponent = {},
    // Title component
    title = {},
    // Subtitle component
    subtitle = {},
    // Content component
    content = {},
    // Media component
    media_component: mediaComponent = {},
    // Button component
    button_component: buttonComponent = {},
  } = component ?? {};

  const { media_layout_vertical: mediaLayoutVertical } = mediaComponent ?? {};

  const { introShown } = useContext(context) ?? {};

  const contentRef = useRef(null);
  const contentInView = useInView(contentRef, { once: true, threshold: 0.4 });

  const paddingStyle = usePadding(paddingComponent);

  const hasContent = [
    eyebrowComponent.display_eyebrow,
    title.display_text,
    subtitle.display_text,
    content.display_text,
    buttonComponent.display_button,
  ].some(Boolean);

  return (
    <section className="rich-content-component component-module" style={{ ...paddingStyle }}>
      {/* Background */}
      <Background asBackground {...backgroundComponent} />

      <motion.div
        className={clsx("rich-content", { horizontal: !mediaLayoutVertical })}
        ref={contentRef}
      >
        {hasContent && (
          <motion.div
            className="rich-content-text"
            initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
            animate={introShown && contentInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
          >
            {/* Eyebrow */}
            <Eyebrow {...eyebrowComponent} />

            {/* Title */}
            <Text {...title} />

            {/* Subtitle */}
            <Text {...subtitle} />

            {/* Content */}
            <Text {...content} />

            {/* Button */}
            {buttonComponent.display_button && !show_secondary_button ? (
              <Button {...buttonComponent} />
            ) : (
              secondary_button_component && (
                <div className="button-container" style={{ justifyContent: buttons_alignment }}>
                  <Button {...buttonComponent} />
                  {show_secondary_button && <Button {...secondary_button_component} />}
                </div>
              )
            )}
          </motion.div>
        )}

        {/* Media */}
        <Media {...mediaComponent} />
      </motion.div>
    </section>
  );
};

RichContent.propTypes = {
  data: RichContentPropTypes,
};

export default RichContent;
